import type { ChatAudience } from './types';

/**
 * One-click first questions for an empty chat. Concrete on purpose: each is a question
 * someone in that seat actually asks, so the first answer shows what ALFIE is for.
 */
export const STARTER_PROMPTS: Record<ChatAudience, readonly string[]> = {
  PROFESSIONAL: [
    'What must a complaint allege to survive a Rule 12(b)(6) motion to dismiss after Twombly and Iqbal?',
    'Summarize the standard for a preliminary injunction in federal court, with the leading cases.',
    'What are the elements of a trademark infringement claim under the Lanham Act?',
    'How do courts decide personal jurisdiction over an out-of-state defendant that sells online?',
    'Outline an opposition to a summary judgment motion on a breach-of-contract claim.',
  ],
  PLAIN: [
    'My landlord kept my security deposit. What can I do to get it back?',
    'I was fired after reporting a safety problem at work. Do I have any rights?',
    'A customer hasn’t paid my invoice for three months. What are my options?',
    'Someone is using my business name. How do I get them to stop?',
    'What should I check before I sign a contract for freelance work?',
  ],
};

/** Inside a matter workspace the useful first questions are about the case files. */
export const MATTER_STARTER_PROMPTS: readonly string[] = [
  'Summarize the key facts in this matter’s case documents.',
  'Build a timeline of what happened, citing the case documents.',
  'What claims do these facts support, and what are the weak points?',
  'What facts or documents are still missing to evaluate this matter?',
];

const AUDIENCE_KEY = 'alfie.chat.audience';

/**
 * The audience this browser last chose — a per-viewer convenience only (the conversation's
 * own audience is stored server-side). Null when never chosen or storage is unavailable.
 */
export function storedAudience(): ChatAudience | null {
  try {
    const v = window.localStorage.getItem(AUDIENCE_KEY);
    return v === 'PROFESSIONAL' || v === 'PLAIN' ? v : null;
  } catch {
    return null;
  }
}

export function rememberAudience(audience: ChatAudience): void {
  try {
    window.localStorage.setItem(AUDIENCE_KEY, audience);
  } catch {
    // Private window or blocked storage: the choice still applies to this visit.
  }
}
